import React, { useState, useEffect } from 'react';

function EditExpense({ expense, categories = [], loadingCategories = false, accounts = [], onSave, onCancel }) {
  const [description, setDescription] = useState(expense?.description || '');
  const [amount, setAmount] = useState(expense?.amount?.toString() || '');
  const [category, setCategory] = useState(expense?.category || '');
  const [date, setDate] = useState(expense?.date || '');
  const [bucketId, setBucketId] = useState(expense?.bucketId || '');
  const [note, setNote] = useState(expense?.note || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Split mode state
  const [isSplit, setIsSplit] = useState(false);
  const [splits, setSplits] = useState([
    { categoryId: '', amount: '' },
    { categoryId: '', amount: '' }
  ]);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [selectedCategoryForConversion, setSelectedCategoryForConversion] = useState('');

  useEffect(() => {
    if (expense) {
      setDescription(expense.description || '');
      setAmount(expense.amount?.toString() || '');
      setDate(expense.date || '');
      setBucketId(expense.bucketId || '');
      setNote(expense.note || '');
      
      // Check if this is a split expense
      if (expense.splits && Array.isArray(expense.splits) && expense.splits.length > 0) {
        setIsSplit(true);
        setSplits(expense.splits.map(split => ({
          categoryId: split.categoryId,
          amount: split.amount.toString()
        })));
        setCategory(''); // Clear category for split expenses
      } else {
        setIsSplit(false);
        setCategory(expense.category || '');
        setSplits([
          { categoryId: '', amount: '' },
          { categoryId: '', amount: '' }
        ]);
      }
    }
  }, [expense]);

  // Calculate split sum in real-time
  const calculateSplitSum = () => {
    return splits.reduce((sum, split) => {
      const splitAmount = parseFloat(split.amount) || 0;
      return sum + splitAmount;
    }, 0);
  };

  const splitSum = calculateSplitSum();
  const totalAmount = parseFloat(amount) || 0;
  const splitSumMatches = Math.abs(splitSum - totalAmount) < 0.01;
  
  // Check if all splits are filled
  const allSplitsFilled = splits.every(s => s.categoryId && s.amount);
  
  // Validation: splits must sum to total when in split mode
  const isSplitValid = !isSplit || (allSplitsFilled && splitSumMatches);

  const handleConvertToSplit = () => {
    // Pre-populate first split with current category and full amount
    setSplits([
      { categoryId: category, amount: amount },
      { categoryId: '', amount: '' }
    ]);
    setIsSplit(true);
    setCategory('');
  };

  const handleConvertToSimple = () => {
    setShowConvertDialog(true);
  };

  const confirmConvertToSimple = () => {
    if (!selectedCategoryForConversion) {
      setError('Please select a category to keep');
      return;
    }
    setCategory(selectedCategoryForConversion);
    setIsSplit(false);
    setSplits([
      { categoryId: '', amount: '' },
      { categoryId: '', amount: '' }
    ]);
    setShowConvertDialog(false);
    setSelectedCategoryForConversion('');
  };

  const cancelConvertToSimple = () => {
    setShowConvertDialog(false);
    setSelectedCategoryForConversion('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!description.trim() || !amount || !date) {
      setError('Description, amount, and date are required');
      return;
    }

    if (!isSplit && !category) {
      setError('Category is required for simple expenses');
      return;
    }

    if (isSplit && !isSplitValid) {
      setError('Please ensure all splits are filled and sum to the total amount');
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum === 0) {
      setError('Amount cannot be zero');
      return;
    }

    setSaving(true);
    
    try {
      const updateData = {
        description: description.trim(),
        amount: amountNum,
        date,
        bucketId: bucketId || null,
        note: note.trim() || null,
        // Preserve existing entry method and template info
        entryMethod: expense.entryMethod || 'manual',
        importTemplate: expense.importTemplate || null
      };

      // Add splits or category based on mode
      if (isSplit) {
        updateData.splits = splits.map(split => ({
          categoryId: split.categoryId,
          amount: parseFloat(split.amount)
        }));
      } else {
        updateData.category = category;
      }

      await onSave(updateData);
    } catch (error) {
      console.error('Failed to update expense:', error);
      setError(error.response?.data?.error || 'Failed to update expense');
    } finally {
      setSaving(false);
    }
  };

  if (!expense) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)'
    }}>
      <div className="card" style={{ 
        width: '450px', 
        margin: 0,
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h3 style={{ 
          color: '#ff6b35', 
          marginBottom: '24px',
          fontSize: '1.5rem',
          fontWeight: '600',
          textAlign: 'center'
        }}>
          Edit Expense
        </h3>

        {error && (
          <div style={{ 
            color: '#ff4757', 
            backgroundColor: 'rgba(255, 71, 87, 0.1)',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid rgba(255, 71, 87, 0.3)'
          }}>
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What did you spend on?"
              required
            />
          </div>
          
          <div className="form-group">
            <label>Amount ($)</label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
            />
          </div>

          {/* Conversion buttons */}
          <div className="form-group">
            {!isSplit ? (
              <button
                type="button"
                onClick={handleConvertToSplit}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'rgba(52, 152, 219, 0.2)',
                  border: '1px solid #3498db',
                  borderRadius: '6px',
                  color: '#3498db',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(52, 152, 219, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(52, 152, 219, 0.2)';
                }}
              >
                🔀 Split this expense across categories
              </button>
            ) : (
              <button
                type="button"
                onClick={handleConvertToSimple}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'rgba(149, 165, 166, 0.2)',
                  border: '1px solid #95a5a6',
                  borderRadius: '6px',
                  color: '#95a5a6',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(149, 165, 166, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(149, 165, 166, 0.2)';
                }}
              >
                ↩️ Convert to simple expense
              </button>
            )}
          </div>
          
          {/* Simple category selection (when not split) */}
          {!isSplit && (
            <div className="form-group">
              <label>Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                required
                disabled={loadingCategories}
              >
                <option value="">
                  {loadingCategories ? 'Loading categories...' : 'Select a category'}
                </option>
                {categories.map(cat => {
                  // Check if this is a subcategory by looking for parentId or parent info
                  const isSubcategory = cat.parentId || cat.parent;
                  const displayName = isSubcategory 
                    ? `  ↳ ${cat.name}` // Indent subcategories with arrow
                    : cat.name;
                  
                  return (
                    <option key={cat.id || cat.name} value={cat.name}>
                      {cat.icon ? `${cat.icon} ${displayName}` : displayName}
                    </option>
                  );
                })}
              </select>
              <div style={{
                fontSize: '12px',
                color: '#888',
                marginTop: '4px'
              }}>
                Choose from any category or subcategory
              </div>
            </div>
          )}

          {/* Split entry interface (when split mode is enabled) */}
          {isSplit && (
            <div className="form-group">
              <label>Category Splits</label>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #404040'
              }}>
                {splits.map((split, index) => (
                  <div key={index} style={{
                    marginBottom: index < splits.length - 1 ? '12px' : '0',
                    paddingBottom: index < splits.length - 1 ? '12px' : '0',
                    borderBottom: index < splits.length - 1 ? '1px solid #404040' : 'none'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#b0b0b0'
                      }}>
                        Split {index + 1}
                      </div>
                      {splits.length > 2 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newSplits = splits.filter((_, i) => i !== index);
                            setSplits(newSplits);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#dc3545',
                            cursor: 'pointer',
                            fontSize: '14px',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#ffe0e0'}
                          onMouseLeave={(e) => e.target.style.background = 'none'}
                          title="Remove this split"
                        >
                          ✕ Remove
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <select
                        value={split.categoryId}
                        onChange={(e) => {
                          const newSplits = [...splits];
                          newSplits[index].categoryId = e.target.value;
                          setSplits(newSplits);
                        }}
                        required
                        disabled={loadingCategories}
                        style={{ flex: 2 }}
                      >
                        <option value="">
                          {loadingCategories ? 'Loading...' : 'Select category'}
                        </option>
                        {categories.map(cat => {
                          const isSubcategory = cat.parentId || cat.parent;
                          const displayName = isSubcategory 
                            ? `  ↳ ${cat.name}`
                            : cat.name;
                          
                          return (
                            <option key={cat.id || cat.name} value={cat.name}>
                              {cat.icon ? `${cat.icon} ${displayName}` : displayName}
                            </option>
                          );
                        })}
                      </select>
                      <input
                        type="number"
                        step="0.01"
                        value={split.amount}
                        onChange={(e) => {
                          const newSplits = [...splits];
                          newSplits[index].amount = e.target.value;
                          setSplits(newSplits);
                        }}
                        placeholder="0.00"
                        required
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>
                ))}
                
                {/* Add Another Split button */}
                <button
                  type="button"
                  onClick={() => {
                    setSplits([...splits, { categoryId: '', amount: '' }]);
                  }}
                  style={{
                    width: '100%',
                    marginTop: '12px',
                    padding: '10px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '2px dashed #404040',
                    borderRadius: '6px',
                    color: '#888',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#ff6b35';
                    e.target.style.color = '#ff6b35';
                    e.target.style.background = 'rgba(255, 107, 53, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#404040';
                    e.target.style.color = '#888';
                    e.target.style.background = 'rgba(255, 255, 255, 0.03)';
                  }}
                >
                  + Add Another Split
                </button>

                {/* Real-time validation display */}
                {isSplit && amount && (
                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    borderRadius: '6px',
                    background: splitSumMatches ? 'rgba(46, 213, 115, 0.1)' : 'rgba(255, 193, 7, 0.1)',
                    border: `1px solid ${splitSumMatches ? 'rgba(46, 213, 115, 0.3)' : 'rgba(255, 193, 7, 0.3)'}`
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '4px'
                    }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#b0b0b0'
                      }}>
                        Total Expense:
                      </div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#f0f0f0'
                      }}>
                        ${totalAmount.toFixed(2)}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#b0b0b0'
                      }}>
                        Split Sum:
                      </div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: splitSumMatches ? '#2ed573' : '#ffc107',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        ${splitSum.toFixed(2)}
                        {splitSumMatches ? (
                          <span style={{ fontSize: '16px' }}>✓</span>
                        ) : (
                          <span style={{ fontSize: '16px' }}>⚠️</span>
                        )}
                      </div>
                    </div>
                    {!splitSumMatches && allSplitsFilled && (
                      <div style={{
                        marginTop: '8px',
                        fontSize: '12px',
                        color: '#ffc107',
                        fontWeight: '500'
                      }}>
                        Split amounts must equal the total expense amount
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#888',
                marginTop: '8px'
              }}>
                Assign amounts to each category (must total ${amount || '0.00'})
              </div>
            </div>
          )}
          
          <div className="form-group">
            <label>Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add any additional details..."
              rows={3}
            />
            <div style={{
              fontSize: '12px',
              color: '#888',
              marginTop: '4px'
            }}>
              Any extra context about this expense
            </div>
          </div>

          {/* Bucket assignment dropdown */}
          {accounts.length > 0 && (
            <div className="form-group">
              <label>Bucket (optional)</label>
              <select
                value={bucketId}
                onChange={(e) => setBucketId(e.target.value)}
              >
                <option value="">No bucket</option>
                {accounts.map(account => (
                  account.buckets && account.buckets.length > 0 && (
                    <optgroup key={account.id} label={account.name}>
                      {account.buckets.map(bucket => (
                        <option key={bucket.id} value={bucket.id}>
                          {bucket.name}
                        </option>
                      ))}
                    </optgroup>
                  )
                ))}
              </select>
              <div style={{
                fontSize: '12px',
                color: '#888',
                marginTop: '4px'
              }}>
                Assign this expense to a savings bucket
              </div>
            </div>
          )}

          {/* Show entry method information */}
          {expense && (expense.entryMethod || expense.importTemplate) && (
            <div style={{
              background: 'rgba(52, 152, 219, 0.1)',
              border: '1px solid rgba(52, 152, 219, 0.3)',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px'
            }}>
              <h4 style={{ 
                color: '#3498db', 
                margin: '0 0 8px 0',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                Entry Information
              </h4>
              <div style={{ fontSize: '13px', color: '#b0b0b0' }}>
                <div>
                  <strong>Entry Method:</strong> {expense.entryMethod === 'manual' ? 'Manual Entry' : 'CSV Import'}
                </div>
                {expense.importTemplate && (
                  <div>
                    <strong>Import Template:</strong> {expense.importTemplate}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Show what's changed */}
          <div style={{
            background: 'rgba(255, 107, 53, 0.1)',
            border: '1px solid rgba(255, 107, 53, 0.2)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <h4 style={{ 
              color: '#ff6b35', 
              margin: '0 0 12px 0',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              Changes Preview
            </h4>
            <div style={{ fontSize: '14px', color: '#b0b0b0' }}>
              {description !== expense.description && (
                <div>Description: <span style={{ color: '#ff4757' }}>{expense.description}</span> → <span style={{ color: '#2ed573' }}>{description}</span></div>
              )}
              {parseFloat(amount) !== Math.abs(expense.amount) && (
                <div>Amount: <span style={{ color: '#ff4757' }}>${Math.abs(expense.amount)}</span> → <span style={{ color: '#2ed573' }}>${amount}</span></div>
              )}
              {!isSplit && category !== expense.category && (
                <div>Category: <span style={{ color: '#ff4757' }}>{expense.category}</span> → <span style={{ color: '#2ed573' }}>{category}</span></div>
              )}
              {isSplit && !expense.splits && (
                <div style={{ color: '#2ed573' }}>Converting to split expense with {splits.length} categories</div>
              )}
              {!isSplit && expense.splits && (
                <div style={{ color: '#2ed573' }}>Converting to simple expense (category: {category})</div>
              )}
              {date !== expense.date && (
                <div>Date: <span style={{ color: '#ff4757' }}>{expense.date}</span> → <span style={{ color: '#2ed573' }}>{date}</span></div>
              )}
              {description === expense.description && 
               parseFloat(amount) === Math.abs(expense.amount) && 
               ((isSplit && expense.splits) || (!isSplit && category === expense.category)) &&
               date === expense.date && (
                <div style={{ color: '#888' }}>No changes made</div>
              )}
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
            <button 
              type="submit" 
              className="btn" 
              style={{ flex: 1 }}
              disabled={saving || loadingCategories || !isSplitValid}
            >
              {saving ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <span style={{ 
                    width: '16px', 
                    height: '16px', 
                    border: '2px solid transparent',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
            <button 
              type="button" 
              onClick={onCancel}
              className="btn" 
              style={{ 
                flex: 1, 
                background: 'linear-gradient(135deg, #666, #555)',
                boxShadow: '0 2px 8px rgba(102, 102, 102, 0.3)'
              }}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* Convert to Simple Dialog */}
      {showConvertDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.9)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1001
        }}>
          <div className="card" style={{ 
            width: '400px', 
            margin: 0
          }}>
            <h3 style={{ 
              color: '#ff6b35', 
              marginBottom: '16px',
              fontSize: '1.3rem',
              fontWeight: '600'
            }}>
              Convert to Simple Expense
            </h3>
            
            <p style={{ 
              color: '#b0b0b0', 
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              Select which category to keep for this expense:
            </p>

            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              {splits.map((split, index) => (
                <label
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    marginBottom: index < splits.length - 1 ? '8px' : '0',
                    background: selectedCategoryForConversion === split.categoryId 
                      ? 'rgba(255, 107, 53, 0.2)' 
                      : 'transparent',
                    border: `1px solid ${selectedCategoryForConversion === split.categoryId ? '#ff6b35' : '#404040'}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedCategoryForConversion !== split.categoryId) {
                      e.currentTarget.style.background = 'rgba(255, 107, 53, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCategoryForConversion !== split.categoryId) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <input
                    type="radio"
                    name="categorySelection"
                    value={split.categoryId}
                    checked={selectedCategoryForConversion === split.categoryId}
                    onChange={(e) => setSelectedCategoryForConversion(e.target.value)}
                    style={{
                      width: '18px',
                      height: '18px',
                      accentColor: '#ff6b35',
                      cursor: 'pointer'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      color: '#f0f0f0', 
                      fontWeight: '500',
                      marginBottom: '4px'
                    }}>
                      {split.categoryId}
                    </div>
                    <div style={{ 
                      color: '#888', 
                      fontSize: '12px'
                    }}>
                      ${parseFloat(split.amount).toFixed(2)}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={confirmConvertToSimple}
                className="btn"
                style={{ flex: 1 }}
                disabled={!selectedCategoryForConversion}
              >
                Convert
              </button>
              <button
                onClick={cancelConvertToSimple}
                className="btn"
                style={{ 
                  flex: 1,
                  background: 'linear-gradient(135deg, #666, #555)',
                  boxShadow: '0 2px 8px rgba(102, 102, 102, 0.3)'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditExpense;